import {
  faBold,
  faChevronUp,
  faItalic,
  faList,
  faSlashForward,
  faTextSize,
  faUnderline
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Emoji from '@tiptap-pro/extension-emoji'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import {
  BubbleMenu,
  type Editor,
  EditorContent,
  useEditor,
  useEditorState
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import clsx from 'clsx'
import type React from 'react'
import { type ReactElement, useEffect, useState } from 'react'
import emojiSuggestion from '~/components/Form/Editor/Emoji/suggestion'
import { AttachmentAddInput } from '~/components/Form/Input/AttachmentAddInput'
import { AttachmentsInput } from '~/components/Form/Input/AttachmentsInput'
import {
  editorBody,
  editorBottom,
  editorBubbleMenu,
  editorButtonToggle,
  editorButtonToggleActive,
  editorContainer,
  editorEmpty,
  editorMenuBottom,
  editorOptions,
  editorOptionsContent,
  editorOptionsContentActive,
  editorOptionsTrigger
} from '~/components/Form/Input/EditorInput.css'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { addPlaceholderExtension } from '../Editor/Placeholder/PlaceholderExtension'
import {
  errorMessage,
  inputError,
  inputLabel,
  inputWrapper
} from './TextInput.css'

type EditorInputProps = React.HTMLProps<HTMLDivElement> &
  InputInFormProps & {
    label?: string
    name?: string
    error?: null | { message: string }
    contextInfo?: string
    hasAttachments?: boolean
    placeholderCategories?: string[]
    options?: ReactElement
    prepend?: ReactElement
    onChange?: (value: string) => void
  }

const extensions = [
  StarterKit,
  Underline,
  Placeholder.configure({
    placeholder: 'Saisir votre message...',
    emptyEditorClass: editorEmpty
  }),
  Emoji.configure({
    enableEmoticons: true,
    suggestion: emojiSuggestion
  })
]

const BaseModifiers = ({ editor }: { editor: Editor }) => {
  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isUnderline: editor.isActive('underline'),
      isHeading: editor.isActive('heading'),
      isList: editor.isActive('bulletList')
    })
  })
  return (
    <>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={clsx(
          editorButtonToggle,
          editorState.isList ? editorButtonToggleActive : null
        )}
      >
        <FontAwesomeIcon icon={faList} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={clsx(
          editorButtonToggle,
          editorState.isItalic ? editorButtonToggleActive : null
        )}
      >
        <FontAwesomeIcon icon={faItalic} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={clsx(
          editorButtonToggle,
          editorState.isUnderline ? editorButtonToggleActive : null
        )}
      >
        <FontAwesomeIcon icon={faUnderline} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={clsx(
          editorButtonToggle,
          editorState.isBold ? editorButtonToggleActive : null
        )}
      >
        <FontAwesomeIcon icon={faBold} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={clsx(
          editorButtonToggle,
          editorState.isHeading ? editorButtonToggleActive : null
        )}
      >
        <FontAwesomeIcon icon={faTextSize} />
      </button>
    </>
  )
}

export const EditorInput = ({
  control,
  register,
  label,
  name,
  error = null,
  setValue,
  hasAttachments = false,
  onChange,
  options,
  prepend,
  watch,
  placeholderCategories
}: EditorInputProps): ReactElement => {
  if (
    register === undefined ||
    control === undefined ||
    setValue === undefined ||
    name === undefined ||
    watch === undefined
  ) {
    throw new Error('EditorInput must live inside a Form component')
  }
  register(name)
  const [showOptions, setShowOptions] = useState(false)
  const content = watch(name)
  const editor = useEditor({
    extensions: [...extensions, addPlaceholderExtension(placeholderCategories)],
    editorProps: {
      attributes: {
        class: editorBody
      }
    },
    content,
    onUpdate: ({ editor }) => {
      setValue(name, editor.getHTML())
      onChange?.(editor.getHTML())
    }
  })

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content)
    }
  }, [content, editor])

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        event.target instanceof HTMLElement &&
        !event.target.closest(`.${editorOptions}`)
      ) {
        setShowOptions(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [])

  return (
    <div className={inputWrapper}>
      {label && (
        <label htmlFor={name} className={inputLabel}>
          {label}
        </label>
      )}
      <div className={clsx(editorContainer, error !== null && inputError)}>
        {prepend}
        <EditorContent editor={editor} />
        <BubbleMenu editor={editor} className={editorBubbleMenu}>
          {editor && <BaseModifiers editor={editor} />}
        </BubbleMenu>
        <div className={editorBottom}>
          {editor && (
            <div className={editorMenuBottom}>
              {hasAttachments && (
                <AttachmentAddInput
                  className={editorButtonToggle}
                  control={control}
                />
              )}
              <BaseModifiers editor={editor} />
              {placeholderCategories && placeholderCategories.length > 0 && (
                <Tooltip
                  direction="top"
                  text="Publipostage"
                  as="button"
                  className={editorButtonToggle}
                  onClick={(e) => {
                    e.preventDefault()
                    editor.commands.insertContent(' /')
                  }}
                >
                  <FontAwesomeIcon icon={faSlashForward} />
                </Tooltip>
              )}
              {options && (
                <div className={editorOptions}>
                  <div
                    className={clsx(
                      editorOptionsContent,
                      showOptions && editorOptionsContentActive
                    )}
                  >
                    {options}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowOptions(!showOptions)}
                    className={editorOptionsTrigger}
                  >
                    <FontAwesomeIcon icon={faChevronUp} />
                    Options
                  </button>
                </div>
              )}
            </div>
          )}
          {hasAttachments && (
            <AttachmentsInput control={control} watch={watch} />
          )}
        </div>
      </div>
      {error !== null && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
